import { Component, ReactNode, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';
import ReactPlayer from 'react-player';
import { Volume2 } from 'lucide-react';
import audioThumbnail from '@assets/images/audio-thumbnail.png';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useTranscript } from '@utils/providers/transcript-context.tsx';

class PlayerErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Inline media player for the transcript side panel. Consumes the same
 * audio-context state as the (previously fixed / draggable) player, so
 * playback stays in sync with the transcript.
 */
export const TranscriptMediaPlayer = () => {
  const { session, handleMediaElementReady } = useTranscript();
  const {
    play,
    onMediaPause,
    onProgress,
    setRef,
    isPlaying,
    playbackRate,
    setDuration,
    isMuted
  } = useAudio();
  const ref = useRef<ReactPlayer>(null);

  useEffect(() => {
    if (ref.current) {
      setRef(ref);
    }
  }, [setRef]);

  // Stable debounced onProgress — avoids recreating the debounce wrapper
  // every render, which leaks closures since onProgress is not memoized.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const debouncedOnProgress = useMemo(
    () =>
      debounce((state: Parameters<typeof onProgress>[0]) => {
        onProgressRef.current(state);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedOnProgress.cancel();
    };
  }, [debouncedOnProgress]);

  // Re-attach peaks whenever wave_info_url changes — notably after
  // regenerate-audio-timeline swaps it to a new versioned blob. onReady alone
  // only fires once per media mount, so without this the editor stays on the
  // old waveform.
  useEffect(() => {
    handleMediaElementReady(session?.wave_info_url);
  }, [handleMediaElementReady, session?.wave_info_url]);

  if (!session?.media_url) return null;

  const isAudio = session?.media_type === 'audio';

  return (
    <div className="relative w-full h-full overflow-hidden">
      <PlayerErrorBoundary>
        <ReactPlayer
          ref={ref}
          url={session.media_url}
          controls={false}
          onPlay={play}
          onPause={onMediaPause}
          playing={isPlaying}
          volume={1}
          muted={isMuted}
          onProgress={debouncedOnProgress}
          playbackRate={playbackRate}
          autoPlay={false}
          className={cn('w-full h-full', isAudio && 'pointer-events-none')}
          progressInterval={300}
          onDuration={setDuration}
          width="100%"
          height="100%"
          fallback={
            <div className="flex justify-center items-center h-full">
              <Volume2 size={35} />
            </div>
          }
          onReady={() => handleMediaElementReady(session?.wave_info_url)}
          onError={() => {
            // Silently handle media loading errors (e.g., 404 for deleted/moved files)
            // This prevents console errors from reaching Sentry
          }}
        />
        {isAudio && (
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
            <img src={audioThumbnail} alt="Audio Thumbnail" width={60} />
          </div>
        )}
      </PlayerErrorBoundary>
      {/* Top / bottom fade — soft white gradient overlays. Sides untouched. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/70 to-transparent"
      />
    </div>
  );
};
